import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiPost } from '../lib/api.ts'

/**
 * A page of the voters a universe matches.
 *
 * Collapsed by default and fetched only when opened. That is not just tidiness:
 * every request here discloses individuals and is audited as such, so it should
 * happen because someone asked, not because a page rendered.
 */

interface Voter {
  person_id: string
  last_name: string | null
  first_name: string | null
  party_code: string | null
  status: string | null
  city: string | null
  zip: string | null
  precinct_id: string | null
  district_cd: string | null
  has_phone: boolean
  participation_count: number
}

interface VotersResponse {
  voters: Voter[]
  next_cursor: string | null
  took_ms: number
}

const PAGE = 25

export function VoterResults({
  campaignId,
  ast,
  enabled,
}: {
  campaignId: string
  ast: unknown
  enabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const [cursors, setCursors] = useState<(string | null)[]>([null])
  const page = cursors.length - 1
  const after = cursors[page]

  const astKey = JSON.stringify(ast)

  const { data, isFetching, error } = useQuery({
    queryKey: ['universe', 'voters', campaignId, astKey, after],
    queryFn: () =>
      apiPost<VotersResponse>('/universe/voters', {
        campaign_id: campaignId,
        ast: JSON.parse(astKey) as unknown,
        limit: PAGE,
        ...(after ? { after } : {}),
      }),
    enabled: open && enabled && astKey !== 'null',
  })

  if (!enabled) return null

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => {
          // Reset paging whenever it is reopened; the filters may have changed.
          setCursors([null])
          setOpen((v) => !v)
        }}
        className="text-sm font-medium text-ink underline-offset-2 hover:underline"
      >
        {open ? 'Hide matching voters' : 'Show matching voters'}
      </button>

      {open ? (
        <div className="mt-3 rounded-lg border border-line bg-surface-raised">
          <p className="border-b border-line px-3 py-2 text-xs text-ink-muted">
            Viewing voter records is logged against this campaign, with who
            viewed them and how many were shown.
          </p>

          {error ? (
            <p className="px-3 py-4 text-sm">
              {error instanceof Error ? error.message : 'Could not load voters.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
                    <th scope="col" className="px-3 py-2 font-semibold">Name</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Party</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Status</th>
                    <th scope="col" className="px-3 py-2 font-semibold">City</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Precinct</th>
                    <th scope="col" className="px-3 py-2 font-semibold">CD</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Phone</th>
                    <th scope="col" className="px-3 py-2 font-semibold">Voted</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetching && !data ? (
                    <tr><td colSpan={8} className="px-3 py-4 text-ink-muted">Loading…</td></tr>
                  ) : (data?.voters.length ?? 0) === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-4 text-ink-muted">No voters match.</td></tr>
                  ) : (
                    data?.voters.map((v) => (
                      <tr key={v.person_id} className="border-b border-line/60">
                        <td className="px-3 py-1.5">
                          {[v.last_name, v.first_name].filter(Boolean).join(', ') || '—'}
                        </td>
                        {/* Party is a plain code, not a colour. The party scale
                            is for charts; colouring a row would make the table
                            read as party-coded. */}
                        <td className="px-3 py-1.5 font-mono text-xs">{v.party_code ?? '—'}</td>
                        <td className="px-3 py-1.5 text-xs">{v.status ?? '—'}</td>
                        <td className="px-3 py-1.5">{v.city ?? '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">{v.precinct_id ?? '—'}</td>
                        <td className="px-3 py-1.5 tabular-nums">{v.district_cd ?? '—'}</td>
                        <td className="px-3 py-1.5 text-xs">{v.has_phone ? 'yes' : '—'}</td>
                        <td className="px-3 py-1.5 tabular-nums">{v.participation_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3 border-t border-line px-3 py-2">
            <button
              type="button"
              disabled={page === 0 || isFetching}
              onClick={() => setCursors((c) => c.slice(0, -1))}
              className="rounded-md border border-line px-2 py-1 text-xs disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!data?.next_cursor || isFetching}
              onClick={() => setCursors((c) => [...c, data!.next_cursor])}
              className="rounded-md border border-line px-2 py-1 text-xs disabled:opacity-40"
            >
              Next
            </button>
            <span className="text-xs text-ink-muted">
              Page {page + 1}
              {data ? ` · ${data.voters.length} shown · ${data.took_ms} ms` : ''}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}
